import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../../providers/TenantProvider';

export interface User {
  id: string;
  userId: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'suspended';
  joinedAt: string;
  lastActiveAt?: string;
  avatarUrl?: string;
}

interface InviteUserInput {
  email: string;
  role: 'admin' | 'member';
}

export function useUsers() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentTenant } = useTenant();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/members`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.members || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently]);

  const inviteUser = useCallback(
    async (input: InviteUserInput) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/members`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify(input),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to invite user');
        }

        // Refresh users list
        await fetchUsers();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently, fetchUsers]
  );

  const updateUserRole = useCallback(
    async (userId: string, role: 'owner' | 'admin' | 'member') => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/members/${userId}/role`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify({ role }),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to update role');
        }

        // Update local state
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const removeUser = useCallback(
    async (userId: string) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/members/${userId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              'x-tenant-id': currentTenant.id,
            },
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to remove user');
        }

        // Update local state
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  const resendInvite = useCallback(
    async (userId: string) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}/members/${userId}/resend-invite`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'x-tenant-id': currentTenant.id,
            },
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to resend invite');
        }

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  return {
    users,
    isLoading,
    error,
    fetchUsers,
    inviteUser,
    updateUserRole,
    removeUser,
    resendInvite,
  };
}
